import React, { useEffect, useMemo, useState } from "react";
import Modal from "../../components/Atomos/Modals/Modal";
import Input from "../../components/Atomos/Inputs/Input";
import CalendarInput from "../../components/Atomos/Inputs/CalendarInput";
import Dropdown from "../../components/Atomos/DropDown/Dropdown";
import Button from "../../components/staff/Button";
import InputText from "../../components/staff/InputText";
import {
  createPayment,
  getOfficeInvoices,
  upsertInvoice,
  type FirmInvoiceDoc,
  type NewPaymentPayload,
} from "../../Services/Billing";
import {
  recordDirectPaymentBalanceMovement,
  resolvePerformedBy,
} from "../../utils/accountingOfficeBalance";
import { useAuth } from "../../store/AuthContext";
import type { AccountingOfficeDetail } from "../../utils/accountingOfficeDetail";
import {
  officeDisplayName,
  officePartyKey,
} from "../../utils/accountingOfficeDetail";
import { generateAccountingPaymentReceiptPDFBlob } from "../../utils/generateAccountingInvoicePDF";
import InvoicePdfPreview from "../../components/Atomos/PdfPreview/InvoicePdfPreview";
import {
  AccountingModalCheckbox,
  AccountingModalError,
  AccountingModalFirmSubtitle,
  AccountingModalFooter,
  AccountingModalSectionTitle,
  AccountingModalTabs,
  displayToIso,
  formatMoney,
  isoToDisplay,
  todayIso,
} from "./accountingModalUi";
import {
  BILLING_CATEGORY_DISPLAY_ORDER,
  getBillingCategoryLabel,
  resolveItemCategory,
  type BillingCategory,
} from "../../utils/billingCategoryUtils";
import {
  deriveBillingStatus,
  formatBillingStatusLabel,
  isCancelledBillingDoc,
  type BillingStatus,
} from "../../utils/billingStatus";
import {
  DIRECT_PAYMENT_CODE,
  DIRECT_PAYMENT_DESCRIPTION,
} from "../../utils/billingConstants";
import { useInvoiceAuditLogger } from "../../utils/invoiceAudit";
import {
  buildInvoicePaymentAppliedChanges,
  buildPaymentConcepts,
  summarizeChanges,
  summarizeConcepts,
} from "../../utils/billingAuditDiff";
import DiscardPaymentRegistrationModal from "./DiscardPaymentRegistrationModal";

type CreatePaymentModalProps = {
  office: AccountingOfficeDetail;
  isOpen: boolean;
  onClose: () => void;
  onRegistered: () => void;
  /** When set, opens on Apply to Invoice with this invoice pre-selected. */
  initialInvoiceId?: string | null;
};

type PaymentTab = "apply" | "direct";

const RECEIPT_PREVIEW_DEBOUNCE_MS = 400;

function num(value: string): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function genId(partyKey: string): string {
  const uuid =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${partyKey}-pay-${uuid}`;
}

function genDirectPaymentReference(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const suffix = String(Date.now()).slice(-5);
  return `DP-${y}${m}${d}-${suffix}`;
}

function invoiceLabel(inv: FirmInvoiceDoc): string {
  const ref = inv.invoiceNumber ?? inv.id;
  const bal = Number(inv.balance ?? 0);
  return `${ref} — ${formatMoney(bal)} due`;
}

type CategoryRow = {
  key: BillingCategory;
  label: string;
  billed: number;
  paid: number;
  balanceDue: number;
  status: BillingStatus;
};

type DisplayCategoryRow = CategoryRow & {
  /** Original balance before pending allocations (used for input limits). */
  baseBalanceDue: number;
};

function allocationStatusClass(status: BillingStatus): string {
  if (status === "paid") {
    return "font-medium text-green-600";
  }
  if (status === "partial") {
    return "font-medium text-gray-font";
  }
  return "font-medium text-red-600";
}

function clampAllocationInput(value: string, maxDue: number): string {
  if (value.trim() === "") {
    return "";
  }
  const parsed = num(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return "0";
  }
  const clamped = Math.min(parsed, Math.max(0, maxDue));
  return String(Math.round(clamped * 100) / 100);
}

export default function CreatePaymentModal({
  office,
  isOpen,
  onClose,
  onRegistered,
  initialInvoiceId = null,
}: CreatePaymentModalProps): React.ReactElement {
  const officeName = officeDisplayName(office);
  const partyKey = officePartyKey(office.id);
  const invoiceReferenceLocked = Boolean(initialInvoiceId?.trim());
  const { logEvent } = useInvoiceAuditLogger();
  const { loggedPerson, tokenDecoded } = useAuth();

  const [tab, setTab] = useState<PaymentTab>("apply");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const [invoices, setInvoices] = useState<FirmInvoiceDoc[]>([]);

  const [receiptDate, setReceiptDate] = useState(todayIso());
  const [selectedInvoiceId, setSelectedInvoiceId] = useState("");
  const [enabledRows, setEnabledRows] = useState<Record<string, boolean>>({});
  const [allocations, setAllocations] = useState<Record<string, string>>({});

  const [processedDate, setProcessedDate] = useState(todayIso());
  const [billingPeriod, setBillingPeriod] = useState(todayIso());
  const [manualAmount, setManualAmount] = useState("0");
  const [paymentDescription, setPaymentDescription] = useState("");
  const [directReference, setDirectReference] = useState("");

  const [receiptPdfBlob, setReceiptPdfBlob] = useState<Blob | null>(null);
  const [receiptPdfLoading, setReceiptPdfLoading] = useState(false);
  const [receiptPdfError, setReceiptPdfError] = useState("");
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const activeInvoices = useMemo(
    () => invoices.filter((i) => !isCancelledBillingDoc(i)),
    [invoices],
  );

  const openInvoices = useMemo(() => {
    const withBalance = activeInvoices.filter(
      (i) => Number(i.balance ?? 0) > 0.005,
    );
    const pinnedId = initialInvoiceId?.trim();
    if (!pinnedId) {
      return withBalance;
    }
    const pinned = activeInvoices.find((i) => i.id === pinnedId);
    if (!pinned || withBalance.some((i) => i.id === pinned.id)) {
      return withBalance;
    }
    return [pinned, ...withBalance];
  }, [activeInvoices, initialInvoiceId]);

  const currentTotalDebt = useMemo(
    () => activeInvoices.reduce((s, i) => s + Number(i.balance ?? 0), 0),
    [activeInvoices],
  );

  const selectedInvoice = useMemo(
    () => invoices.find((i) => i.id === selectedInvoiceId),
    [invoices, selectedInvoiceId],
  );

  const invoiceOptions = useMemo(
    () =>
      openInvoices.map((inv) => ({
        id: inv.id,
        name: invoiceLabel(inv),
      })),
    [openInvoices],
  );

  const selectedInvoiceLabel =
    invoiceOptions.find((o) => o.id === selectedInvoiceId)?.name ?? "";

  const categoryRows = useMemo<CategoryRow[]>(() => {
    if (!selectedInvoice) {
      return [];
    }
    const items = selectedInvoice.items ?? [];
    const total = Number(selectedInvoice.total) || 0;
    const balance = Number(
      selectedInvoice.balance ?? total - (selectedInvoice.totalPaid ?? 0),
    );
    const hasItemPaid = items.some((it) => it.paid != null);
    const ratio = total > 0 ? balance / total : 0;
    const byCat: Partial<
      Record<BillingCategory, { billed: number; paid: number }>
    > = {};
    for (const it of items) {
      const category = resolveItemCategory(it, selectedInvoice);
      if (!category) {
        continue;
      }
      const billed = (Number(it.amount) || 0) * (Number(it.quantity) || 1);
      if (!byCat[category]) {
        byCat[category] = { billed: 0, paid: 0 };
      }
      byCat[category]!.billed += billed;
      byCat[category]!.paid += hasItemPaid ? Number(it.paid) || 0 : 0;
    }
    const categoryOrder = new Map(
      BILLING_CATEGORY_DISPLAY_ORDER.map((key, index) => [key, index]),
    );
    return (
      Object.entries(byCat) as [
        BillingCategory,
        { billed: number; paid: number },
      ][]
    )
      .sort(
        ([a], [b]) =>
          (categoryOrder.get(a) ?? 999) - (categoryOrder.get(b) ?? 999),
      )
      .map(([key, v]) => {
      const balanceDue = hasItemPaid
        ? Math.round(Math.max(0, v.billed - v.paid) * 100) / 100
        : Math.round(v.billed * ratio * 100) / 100;
      return {
        key,
        label: getBillingCategoryLabel(key),
        billed: v.billed,
        paid: v.paid,
        balanceDue,
        status: deriveBillingStatus(v.billed, v.paid),
      };
    });
  }, [selectedInvoice]);

  const displayCategoryRows = useMemo<DisplayCategoryRow[]>(() => {
    return categoryRows.map((row) => {
      const pendingAlloc = enabledRows[row.key]
        ? num(allocations[row.key])
        : 0;
      const projectedPaid = row.paid + pendingAlloc;
      return {
        ...row,
        baseBalanceDue: row.balanceDue,
        balanceDue:
          Math.max(
            0,
            Math.round((row.balanceDue - pendingAlloc) * 100) / 100,
          ),
        status: deriveBillingStatus(row.billed, projectedPaid),
      };
    });
  }, [categoryRows, enabledRows, allocations]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    setTab("apply");
    setSaving(false);
    setError("");
    setPreviewOpen(false);
    setDiscardConfirmOpen(false);
    setReceiptDate(todayIso());
    setProcessedDate(todayIso());
    setBillingPeriod(todayIso());
    setManualAmount("0");
    setPaymentDescription("");
    setDirectReference(genDirectPaymentReference());

    let cancelled = false;
    (async () => {
      const data = await getOfficeInvoices(office.id);
      if (cancelled) {
        return;
      }
      setInvoices(data);
      const preferredId = initialInvoiceId?.trim();
      const preferred = preferredId
        ? data.find((i) => i.id === preferredId)
        : undefined;
      const firstOpen = data.find((i) => Number(i.balance ?? 0) > 0.005);
      setSelectedInvoiceId(preferred?.id ?? firstOpen?.id ?? "");
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, office.id, initialInvoiceId]);

  useEffect(() => {
    const enabled: Record<string, boolean> = {};
    const alloc: Record<string, string> = {};
    for (const row of categoryRows) {
      enabled[row.key] = true;
      alloc[row.key] = "0";
    }
    setEnabledRows(enabled);
    setAllocations(alloc);
  }, [categoryRows]);

  const totalCaptured = useMemo(() => {
    return categoryRows.reduce(
      (s, row) => s + (enabledRows[row.key] ? num(allocations[row.key]) : 0),
      0,
    );
  }, [categoryRows, enabledRows, allocations]);

  const discardInvoiceNumber = useMemo(() => {
    if (tab !== "apply" || !selectedInvoice) {
      return undefined;
    }
    return selectedInvoice.invoiceNumber ?? selectedInvoice.id;
  }, [tab, selectedInvoice]);

  const requestClose = () => {
    if (saving) {
      return;
    }
    setDiscardConfirmOpen(true);
  };

  const confirmDiscard = () => {
    setDiscardConfirmOpen(false);
    onClose();
  };

  const cancelDiscard = () => {
    setDiscardConfirmOpen(false);
  };

  const receiptInput = useMemo(() => {
    if (tab === "apply") {
      const lineItems = categoryRows
        .filter((row) => enabledRows[row.key] && num(allocations[row.key]) > 0)
        .map((row) => ({
          label: row.label,
          amount: num(allocations[row.key]),
        }));
      const total = lineItems.reduce((s, i) => s + i.amount, 0);
      return {
        office,
        paymentDate: receiptDate,
        referenceNumber: selectedInvoice?.invoiceNumber ?? selectedInvoice?.id,
        description: paymentDescription.trim(),
        lineItems,
        totalAmount: total,
        currency: selectedInvoice?.currency ?? "USD",
        fiscalYear: selectedInvoice?.context?.fiscalYear,
      };
    }

    const amount = num(manualAmount);
    const label = paymentDescription.trim() || DIRECT_PAYMENT_DESCRIPTION;
    return {
      office,
      paymentDate: processedDate,
      referenceNumber: directReference || undefined,
      description: paymentDescription.trim(),
      lineItems: amount > 0 ? [{ label, amount }] : [],
      totalAmount: amount,
      currency: "USD",
    };
  }, [
    tab,
    categoryRows,
    enabledRows,
    allocations,
    office,
    receiptDate,
    selectedInvoice,
    paymentDescription,
    manualAmount,
    processedDate,
    directReference,
  ]);

  const receiptSignature = useMemo(
    () => JSON.stringify(receiptInput),
    [receiptInput],
  );

  useEffect(() => {
    if (!isOpen || !previewOpen || receiptInput.totalAmount <= 0) {
      setReceiptPdfBlob(null);
      setReceiptPdfLoading(false);
      setReceiptPdfError("");
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setReceiptPdfLoading(true);
        setReceiptPdfError("");
        try {
          const blob =
            await generateAccountingPaymentReceiptPDFBlob(receiptInput);
          if (cancelled) {
            return;
          }
          setReceiptPdfBlob(blob);
        } catch {
          if (!cancelled) {
            setReceiptPdfError("Unable to load payment receipt preview.");
            setReceiptPdfBlob(null);
          }
        } finally {
          if (!cancelled) {
            setReceiptPdfLoading(false);
          }
        }
      })();
    }, RECEIPT_PREVIEW_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, previewOpen, receiptSignature]);

  const handleRegister = async () => {
    setError("");

    if (tab === "apply") {
      if (!selectedInvoice) {
        setError("Select an invoice to apply the payment to.");
        return;
      }
      if (totalCaptured <= 0) {
        setError("Enter at least one allocation amount.");
        return;
      }
      for (const row of categoryRows) {
        if (!enabledRows[row.key]) {
          continue;
        }
        const alloc = num(allocations[row.key]);
        if (alloc > row.balanceDue + 0.005) {
          setError(
            `${row.label} allocation (${formatMoney(alloc)}) exceeds the balance due (${formatMoney(row.balanceDue)}).`,
          );
          return;
        }
      }
      const invoiceBalance = Number(selectedInvoice.balance ?? 0);
      if (totalCaptured > invoiceBalance + 0.005) {
        setError(
          `Total captured (${formatMoney(totalCaptured)}) exceeds the invoice balance (${formatMoney(invoiceBalance)}).`,
        );
        return;
      }

      setSaving(true);
      try {
        const total = Number(selectedInvoice.total) || 0;
        const items = (selectedInvoice.items ?? []).map((it) => ({ ...it }));

        const anyItemPaid = items.some((it) => it.paid != null);
        const priorPaid = Number(selectedInvoice.totalPaid) || 0;
        items.forEach((it) => {
          if (it.paid != null) {
            return;
          }
          const billed = (Number(it.amount) || 0) * (Number(it.quantity) || 1);
          it.paid =
            !anyItemPaid && priorPaid > 0 && total > 0
              ? Math.round(billed * (priorPaid / total) * 100) / 100
              : 0;
        });

        const allocationAudit: NewPaymentPayload["allocations"] = [];
        for (const row of categoryRows) {
          const alloc = enabledRows[row.key] ? num(allocations[row.key]) : 0;
          if (alloc <= 0) {
            continue;
          }
          allocationAudit.push({
            invoiceId: selectedInvoice.id,
            amount: alloc,
            category: row.label,
          });
          const idxs: number[] = [];
          items.forEach((it, i) => {
            if (resolveItemCategory(it, selectedInvoice) === row.key) {
              idxs.push(i);
            }
          });
          if (idxs.length === 0) {
            continue;
          }
          const remainders = idxs.map((i) => {
            const it = items[i];
            const billed =
              (Number(it.amount) || 0) * (Number(it.quantity) || 1);
            return Math.max(0, billed - (Number(it.paid) || 0));
          });
          const remTotal = remainders.reduce((s, r) => s + r, 0);
          idxs.forEach((i, k) => {
            const share =
              remTotal > 0
                ? alloc * (remainders[k] / remTotal)
                : alloc / idxs.length;
            items[i].paid =
              Math.round(((Number(items[i].paid) || 0) + share) * 100) / 100;
          });
        }

        const newTotalPaid =
          Math.round(
            items.reduce((s, it) => s + (Number(it.paid) || 0), 0) * 100,
          ) / 100;
        const newBalance =
          Math.round((total - newTotalPaid) * 100) / 100;

        const trimmedDescription = paymentDescription.trim();

        const paymentId = genId(partyKey);
        const payment: NewPaymentPayload = {
          id: paymentId,
          type: "payment",
          partyKey,
          partyType: "office",
          partyId: office.id,
          currency: selectedInvoice.currency ?? "USD",
          amount: totalCaptured,
          method: "Other",
          reference: selectedInvoice.invoiceNumber ?? selectedInvoice.id,
          paymentDate: receiptDate,
          ...(trimmedDescription ? { description: trimmedDescription } : {}),
          allocations: allocationAudit.length
            ? allocationAudit
            : [{ invoiceId: selectedInvoice.id, amount: totalCaptured }],
        };
        await createPayment(payment);
        const paymentConcepts = buildPaymentConcepts(payment);
        void logEvent({
          billingDocId: paymentId,
          billingDocType: "payment",
          partyKey,
          partyId: office.id,
          officeId: office.id,
          firmId: String(office.firm ?? "") || null,
          invoiceNumber: selectedInvoice.invoiceNumber ?? selectedInvoice.id,
          action: "created",
          concepts: paymentConcepts,
          detail: `payment ${totalCaptured} ${selectedInvoice.currency ?? "USD"} applied to invoice · ${summarizeConcepts(paymentConcepts)}`,
        });

        const updatedInvoice: FirmInvoiceDoc = {
          ...selectedInvoice,
          items,
          totalPaid: newTotalPaid,
          balance: newBalance,
          status: deriveBillingStatus(total, newTotalPaid),
        };
        await upsertInvoice(updatedInvoice);
        const invoicePaymentChanges = buildInvoicePaymentAppliedChanges(
          selectedInvoice,
          updatedInvoice,
        );
        const conceptSummary = summarizeConcepts(paymentConcepts);
        const statusDetail = `${formatBillingStatusLabel(selectedInvoice.status ?? "")} → ${formatBillingStatusLabel(updatedInvoice.status ?? "")}`;
        void logEvent({
          billingDocId: selectedInvoice.id,
          billingDocType: "invoice",
          partyKey,
          partyId: office.id,
          officeId: office.id,
          firmId: String(office.firm ?? "") || null,
          invoiceNumber: selectedInvoice.invoiceNumber ?? selectedInvoice.id,
          contextKind: selectedInvoice.context?.kind,
          action: "edited",
          changes: invoicePaymentChanges,
          concepts: paymentConcepts,
          detail: conceptSummary
            ? `payment applied · status ${statusDetail} · ${conceptSummary}`
            : `payment applied · status ${statusDetail} · ${summarizeChanges(invoicePaymentChanges)}`,
        });

        onRegistered();
        onClose();
      } catch {
        setError("Unable to register the payment. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    const amount = num(manualAmount);
    if (amount <= 0) {
      setError("Enter a payment amount.");
      return;
    }
    setSaving(true);
    try {
      const trimmedDescription = paymentDescription.trim();
      const directPaymentId = genId(partyKey);
      const payment: NewPaymentPayload = {
        id: directPaymentId,
        type: "payment",
        partyKey,
        partyType: "office",
        partyId: office.id,
        currency: "USD",
        amount,
        method: "Other",
        code: DIRECT_PAYMENT_CODE,
        reference: directReference || undefined,
        description: trimmedDescription || DIRECT_PAYMENT_DESCRIPTION,
        paymentDate: processedDate,
        allocations: [],
        memo: billingPeriod ? `Billing period: ${billingPeriod}` : undefined,
      };
      const { performedById, performedByName } = resolvePerformedBy(
        loggedPerson,
        tokenDecoded,
      );
      const sourceLabel = [
        trimmedDescription || DIRECT_PAYMENT_DESCRIPTION,
        directReference,
      ]
        .filter(Boolean)
        .join(" · ");

      await recordDirectPaymentBalanceMovement({
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        paymentId: directPaymentId,
        amount,
        sourceLabel,
        performedById,
        performedByName,
      });

      await createPayment(payment);
      const directConcepts = buildPaymentConcepts(payment);
      void logEvent({
        billingDocId: directPaymentId,
        billingDocType: "payment",
        partyKey,
        partyId: office.id,
        officeId: office.id,
        firmId: String(office.firm ?? "") || null,
        action: "created",
        concepts: directConcepts,
        detail: `direct payment ${amount} USD · ${summarizeConcepts(directConcepts)}`,
      });
      onRegistered();
      onClose();
    } catch {
      setError("Unable to register the payment. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const previewAmount = tab === "apply" ? totalCaptured : num(manualAmount);

  return (
    <>
    <Modal
      isModalOpen={isOpen}
      toggleModal={requestClose}
      title="Payment Registration"
      compact
      compactBodyScrolls
      className="max-w-3xl"
    >
      <div className="flex flex-col gap-5">
        <AccountingModalFirmSubtitle>
          Capture transaction ledger abonos · {officeName}
        </AccountingModalFirmSubtitle>

        <AccountingModalTabs
          tabs={[
            { key: "apply", label: "Apply to Invoice" },
            { key: "direct", label: "Direct Payment (No Invoice)" },
          ]}
          active={tab}
          onChange={(key) => {
            setTab(key);
            setError("");
          }}
        />

        {tab === "apply" ? (
          <div className="flex flex-col gap-5">
            <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
              <CalendarInput
                title="Receipt Date"
                value={isoToDisplay(receiptDate)}
                defaultValue={isoToDisplay(receiptDate)}
                onChange={(date: string) => setReceiptDate(displayToIso(date))}
                type="single"
                error={false}
              />
              <Dropdown
                title="Invoice Number Reference"
                data={invoiceOptions}
                selectedObjst={selectedInvoiceLabel}
                getid
                disableSort
                disabled={invoiceReferenceLocked}
                disableInput={invoiceReferenceLocked}
                seter={(selected: { id?: string } | null) => {
                  if (invoiceReferenceLocked) {
                    return;
                  }
                  setSelectedInvoiceId(selected?.id ?? "");
                }}
                error={false}
              />
            </div>

            <div className="flex flex-wrap items-center justify-between gap-4">
              <AccountingModalSectionTitle>
                Distribute Amount Allocation
              </AccountingModalSectionTitle>
              <span className="text-base text-gray-font">
                Current Total Debt:{" "}
                <span className="font-medium">
                  {formatMoney(currentTotalDebt)}
                </span>
              </span>
            </div>

            {selectedInvoice ? (
              <div className="table-responsive tabledata w-full overflow-x-auto">
                <table className="table min-w-full w-max">
                  <thead>
                    <tr className="bg-accent-Default-dark text-white">
                      <th className="px-4 py-2 text-left text-xs font-medium">
                        Category Concept
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium">
                        Total Billed
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium">
                        Balance Due
                      </th>
                      <th className="px-4 py-2 text-center text-xs font-medium">
                        Status
                      </th>
                      <th className="px-4 py-2 text-right text-xs font-medium">
                        Capture Allocation ($)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayCategoryRows.map((row) => (
                      <tr
                        key={row.key}
                        className="even:bg-gray-100 text-gray-font text-xs"
                      >
                        <td className="px-4 py-2">
                          <AccountingModalCheckbox
                            checked={enabledRows[row.key] ?? false}
                            onChange={(checked) =>
                              setEnabledRows((prev) => ({
                                ...prev,
                                [row.key]: checked,
                              }))
                            }
                            label={row.label}
                          />
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatMoney(row.billed)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums">
                          {formatMoney(row.balanceDue)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={allocationStatusClass(row.status)}>
                            {formatBillingStatusLabel(row.status)}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right">
                          <InputText
                            type="number"
                            value={allocations[row.key] ?? "0"}
                            disabled={
                              !enabledRows[row.key] || row.baseBalanceDue <= 0
                            }
                            min={0}
                            max={row.baseBalanceDue}
                            onChange={(value) =>
                              setAllocations((prev) => ({
                                ...prev,
                                [row.key]: clampAllocationInput(
                                  value,
                                  row.baseBalanceDue,
                                ),
                              }))
                            }
                            inputClassName="text-right tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-gray-200 text-gray-font">
                      <td colSpan={4} className="px-4 py-3 text-right">
                        <span className="text-base font-medium text-primary">
                          Total Captured Payment Amount:
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-base font-medium tabular-nums">
                        {formatMoney(totalCaptured)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            ) : (
              <div className="rounded-md border border-dashed border-gray-300 px-4 py-6 text-center text-base text-gray-font">
                This office has no outstanding invoices to apply a payment to.
                Use the Direct Payment tab instead.
              </div>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <Input
              title="Reference Number"
              value={directReference}
              disabled
              onChange={() => {}}
              error={false}
            />
            <CalendarInput
              title="Processed Date"
              value={isoToDisplay(processedDate)}
              defaultValue={isoToDisplay(processedDate)}
              onChange={(date: string) => setProcessedDate(displayToIso(date))}
              type="single"
              error={false}
            />
            <CalendarInput
              title="Billing Period"
              value={isoToDisplay(billingPeriod)}
              defaultValue={isoToDisplay(billingPeriod)}
              onChange={(date: string) => setBillingPeriod(displayToIso(date))}
              type="single"
              error={false}
            />
            <Input
              title="Amount Paid"
              type="number"
              value={manualAmount}
              onChange={(e) => setManualAmount(e.target.value)}
              error={false}
            />
          </div>
        )}

        <Input
          title="Description"
          value={paymentDescription}
          placeholder={
            tab === "direct"
              ? DIRECT_PAYMENT_DESCRIPTION
              : "Optional payment description"
          }
          onChange={(e) => setPaymentDescription(e.target.value)}
          error={false}
        />

        <div className="border-t border-gray-200 pt-3">
          <button
            type="button"
            className="flex w-full items-center justify-between gap-2 text-left"
            onClick={() => setPreviewOpen((v) => !v)}
          >
            <span className="text-base font-medium text-primary">
              Preview Payment Receipt{" "}
              <span className="font-normal text-gray-font">
                Auto-generates based on allocation
              </span>
            </span>
            <span className="text-gray-font">{previewOpen ? "▲" : "▼"}</span>
          </button>
          {previewOpen && (
            <div className="mt-4 overflow-hidden rounded border border-gray-200 bg-white">
              {receiptPdfError ? (
                <div className="flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-red-600">
                  {receiptPdfError}
                </div>
              ) : previewAmount <= 0 ? (
                <div className="flex min-h-[28rem] items-center justify-center px-4 text-center text-sm text-gray-font">
                  Enter an allocation amount to preview the receipt.
                </div>
              ) : (
                <InvoicePdfPreview
                  pdfBlob={receiptPdfBlob}
                  loading={receiptPdfLoading}
                  loadingMessage="Generating payment receipt preview..."
                  emptyMessage="No preview available."
                />
              )}
            </div>
          )}
        </div>

        <AccountingModalError message={error} />

        <AccountingModalFooter
          onCancel={requestClose}
          onSubmit={handleRegister}
          submitText="Register"
          saving={saving}
          extraLeft={
            <Button
              text="View Sent Receipts"
              color="primary"
              variant="outlined"
              size="fixed"
              onClick={() => setPreviewOpen(true)}
              disabled={saving}
            />
          }
        />
      </div>
    </Modal>
    <DiscardPaymentRegistrationModal
      isOpen={discardConfirmOpen}
      invoiceNumber={discardInvoiceNumber}
      onGoBack={cancelDiscard}
      onConfirmExit={confirmDiscard}
    />
    </>
  );
}
